import type {
  CalibrationProfile,
  FrameContext,
  ModuleId,
  SessionMode,
  TrainingModule,
  TrialPlan,
  TrialResult,
} from '../core/types';

export type ResponseKind = 'single' | 'multi-select';

export type ResponseVisual =
  | 'vehicle-convertible'
  | 'vehicle-box-truck'
  | 'vehicle-sedan'
  | 'vehicle-suv'
  | 'vehicle-hatchback'
  | 'arrow-up'
  | 'arrow-right'
  | 'arrow-down'
  | 'arrow-left'
  | 'sweep-inward'
  | 'sweep-outward'
  | 'orientation-vertical'
  | 'orientation-horizontal'
  | 'location';

export interface ResponseOption {
  /** Canonical value stored in TrialResult.response. */
  value: string;
  /** Optional keyboard shortcut. Pointer controls remain the primary UI. */
  keys?: readonly string[];
  /** Kept for optional keyboard affordances; never used as the main label. */
  keyLabel?: string;
  label: string;
  visual?: ResponseVisual;
  /** Optional position for a response control over the stimulus canvas. */
  position?: { xPercent: number; yPercent: number };
}

export interface ModuleFeedback {
  title: string;
  detail: string;
  correctLabel: string;
}

export interface TrainingFrameContext extends FrameContext {
  pixelsPerDegree: number;
  calibration?: CalibrationProfile;
  selectedResponses?: readonly string[];
}

export interface RuntimeTrainingModule<TStimulus = unknown> extends TrainingModule<TStimulus> {
  readonly shortLabel: string;
  readonly evidenceLabel: string;
  /** Some published paradigms deliberately omit trial-by-trial feedback. */
  readonly showTrainingFeedback?: boolean;
  readonly instructions: readonly string[];
  readonly responseKind: ResponseKind;
  readonly selectionCount?: number;
  /** Submit immediately when the final multi-step choice has been made. */
  readonly autoSubmitMultiSelect?: boolean;
  /** Defaults to timings.stimulusMs. Enables stimulus-locked RT tasks. */
  getResponseOnsetMs?(trial: TrialPlan<TStimulus>): number;
  getResponseOptions(
    trial: TrialPlan<TStimulus>,
    selected?: readonly string[],
  ): readonly ResponseOption[];
  /** Maps a pointer position on the Canvas to a canonical response value. */
  getCanvasResponse?(
    trial: TrialPlan<TStimulus>,
    frame: TrainingFrameContext,
    xCss: number,
    yCss: number,
    canvasRect: DOMRectReadOnly,
    selected: readonly string[],
  ): string | undefined;
  getResponsePrompt?(
    trial: TrialPlan<TStimulus>,
    selected: readonly string[],
  ): string;
  serializeSelections?(selected: readonly string[]): string;
  /** A canonical timeout response, used for valid no-go trials. */
  getTimeoutResponse?(trial: TrialPlan<TStimulus>): string | undefined;
  getFeedback(
    trial: TrialPlan<TStimulus>,
    response: string | undefined,
    correct: boolean,
  ): ModuleFeedback;
  renderFeedback?(
    context: CanvasRenderingContext2D,
    frame: TrainingFrameContext,
    trial: TrialPlan<TStimulus>,
    response: string | undefined,
  ): void;
  getResultMetadata?(
    trial: TrialPlan<TStimulus>,
    response: string | undefined,
  ): Record<string, number | string | boolean | null>;
  getThreshold?(results: readonly TrialResult[]): {
    value: number;
    unit: 'speed' | 'arcmin' | 'percent' | 'ms';
  } | undefined;
}

// The runtime deliberately erases the stimulus generic so heterogeneous modules
// can share one deterministic session queue.
export type AnyRuntimeTrainingModule = RuntimeTrainingModule<any>;

export interface ModuleInfo {
  id: ModuleId;
  label: string;
  shortLabel: string;
  description: string;
  evidenceLabel: string;
  durationLabel: string;
  primaryAction: string;
}

export function isAssessmentMode(mode: SessionMode): boolean {
  return mode !== 'training' && mode !== 'familiarization';
}

export function canonicalResponse(value: string | string[]): string {
  return Array.isArray(value) ? [...value].sort().join(',') : value;
}

export function responseIsCorrect(plan: TrialPlan, response: string | undefined): boolean {
  if (response === undefined) return false;
  if (Array.isArray(plan.correctResponse)) return plan.correctResponse.includes(response);
  return plan.correctResponse === response;
}
